import React, { useRef } from 'react';

import { CODE_LENGTH } from '../../../wallet-cash-out/use-wallet-cash-out';

import styles from './styles.module.scss';

interface IProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  label: string;
}

/**
 * The emailed code as one box per digit: typing moves to the next box, Backspace goes back, and a
 * pasted or autofilled code fills every box.
 */
export const CodeBoxes = ({ value, onChange, onSubmit, label }: IProps): React.ReactElement => {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value[index] ?? '');

  const focus = (index: number) => inputs.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))]?.focus();

  const fill = (index: number, typed: string) => {
    const clean = typed.replace(/\D/g, '');
    if (!clean) {
      return;
    }
    const next = (value.slice(0, index) + clean).slice(0, CODE_LENGTH);
    onChange(next);
    focus(next.length);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        onChange(value.slice(0, index));
      } else if (index > 0) {
        onChange(value.slice(0, index - 1));
        focus(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      focus(index - 1);
    } else if (event.key === 'ArrowRight') {
      focus(index + 1);
    } else if (event.key === 'Enter' && value.length === CODE_LENGTH) {
      onSubmit();
    }
  };

  return (
    <div className={styles.codeBoxes} role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <input
          // The boxes are positional and never reorder.
          key={index}
          ref={element => {
            inputs.current[index] = element;
          }}
          className={styles.codeBox}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={index === 0 ? CODE_LENGTH : 1}
          value={digit}
          aria-label={`${label} ${index + 1}`}
          autoFocus={index === 0}
          onFocus={event => event.target.select()}
          onChange={event => fill(Math.min(index, value.length), event.target.value)}
          onPaste={event => {
            event.preventDefault();
            fill(0, event.clipboardData.getData('text'));
          }}
          onKeyDown={event => handleKeyDown(index, event)}
        />
      ))}
    </div>
  );
};
